// ---------------------------------------------------------------------------
// /campaigns/tz-scheduling — route (OPUS-D13)
// ---------------------------------------------------------------------------
// Dashboard page for managing per-(campaign, affiliate) IANA timezone rules
// (feature #75). Three panels:
//   1. Upsert form  — TzSchedulingForm
//   2. Rules list   — existing rows for the current campaign + per-row delete
//   3. Resolve      — UTC timestamp → localHour / localDay / localTz
//
// Dual-export: a file-route for when the scaffold owner enables file-based
// routing, and a programmatic factory for the current manual registration.
// ---------------------------------------------------------------------------

import type { RootRoute } from "@tanstack/react-router";
import { createRoute } from "@tanstack/react-router";
import {
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useId, useState } from "react";
import { toast } from "sonner";
import { ApiError } from "@/api-client";
import { TzSchedulingForm } from "@/features/tz-scheduling/TzSchedulingForm";
import {
	CAMPAIGN_DEFAULT_AFFILIATE,
	deleteTzRule,
	describeSource,
	formatTimestamp,
	labelAffiliate,
	listTzRules,
	resolveTz,
	resolveFormSchema,
	tzSchedulingQueryKeys,
	upsertTzRule,
	type ResolveTzResponse,
	type TzRuleView,
} from "@/features/tz-scheduling/api";

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function TzSchedulingPage() {
	const [campaignId, setCampaignId] = useState<string>("");
	const queryClient = useQueryClient();

	// --- Rules list -----------------------------------------------------------
	const rulesQuery = useQuery<TzRuleView[], Error>({
		queryKey: tzSchedulingQueryKeys.list(campaignId),
		queryFn: ({ signal }) => listTzRules(campaignId, signal),
		enabled: campaignId.trim().length > 0,
		staleTime: 10_000,
		refetchOnWindowFocus: false,
		retry: 1,
	});

	// --- Upsert mutation ------------------------------------------------------
	const upsert = useMutation({
		mutationFn: upsertTzRule,
		onSuccess: (row) => {
			toast.success(
				`Saved ${row.tz} for ${labelAffiliate(row.affiliate_id)}.`,
			);
			void queryClient.invalidateQueries({
				queryKey: tzSchedulingQueryKeys.list(row.campaign_id),
			});
		},
		onError: (err: unknown) => {
			toast.error(friendlyError(err, "Save failed."));
		},
	});

	// --- Delete mutation ------------------------------------------------------
	const remove = useMutation({
		mutationFn: deleteTzRule,
		onSuccess: (_data, vars) => {
			toast.success(
				`Removed rule for ${labelAffiliate(vars.affiliateId)}.`,
			);
			void queryClient.invalidateQueries({
				queryKey: tzSchedulingQueryKeys.list(vars.campaignId),
			});
		},
		onError: (err: unknown) => {
			toast.error(friendlyError(err, "Delete failed."));
		},
	});

	return (
		<main className="mx-auto w-full max-w-[1100px] px-6 py-10">
			<header className="mb-8">
				<h1 className="text-brand-gradient text-3xl font-semibold tracking-tight sm:text-4xl">
					Timezone scheduling
				</h1>
				<p className="mt-2 max-w-2xl text-sm text-zinc-500">
					Pin a specific IANA timezone to each (campaign, affiliate) pair.
					The scheduler uses these rules to decide day-parting, quiet hours,
					and send windows. Unmatched pairs fall back to the campaign
					default, then to UTC.
				</p>
			</header>

			<section className="mb-10">
				<SectionHeading
					title="Add or update a rule"
					subtitle="Upsert a timezone for a campaign, optionally scoped to a single affiliate."
				/>
				<TzSchedulingForm
					initialCampaignId={campaignId}
					onCampaignChange={setCampaignId}
					isSubmitting={upsert.isPending}
					onSubmit={(parsed) => {
						setCampaignId(parsed.campaignId);
						upsert.mutate(parsed);
					}}
				/>
			</section>

			<section className="mb-10">
				<SectionHeading
					title="Existing rules"
					subtitle={
						campaignId.trim().length === 0
							? "Enter a campaign ID above to list its timezone rules."
							: `Rules pinned to campaign ${campaignId}.`
					}
				/>
				<RulesTable
					campaignId={campaignId}
					isLoading={rulesQuery.isLoading && rulesQuery.isFetching}
					isError={rulesQuery.isError}
					error={rulesQuery.error ?? null}
					rows={rulesQuery.data ?? []}
					onRetry={() => rulesQuery.refetch()}
					onDelete={(row) =>
						remove.mutate({
							campaignId: row.campaign_id,
							affiliateId: row.affiliate_id,
						})
					}
					deletingKey={
						remove.isPending && remove.variables
							? `${remove.variables.campaignId}::${remove.variables.affiliateId}`
							: null
					}
				/>
			</section>

			<section>
				<SectionHeading
					title="Resolve a UTC moment"
					subtitle="Given a UTC timestamp, show the local hour/day for the pair."
				/>
				<ResolvePanel campaignId={campaignId} onCampaignChange={setCampaignId} />
			</section>
		</main>
	);
}

// ---------------------------------------------------------------------------
// Section heading primitive
// ---------------------------------------------------------------------------

interface SectionHeadingProps {
	title: string;
	subtitle?: string;
}

function SectionHeading({ title, subtitle }: SectionHeadingProps) {
	return (
		<div className="mb-4">
			<h2 className="text-lg font-semibold text-zinc-800">{title}</h2>
			{subtitle ? (
				<p className="mt-0.5 text-sm text-zinc-500">{subtitle}</p>
			) : null}
		</div>
	);
}

// ---------------------------------------------------------------------------
// Rules table
// ---------------------------------------------------------------------------

interface RulesTableProps {
	campaignId: string;
	isLoading: boolean;
	isError: boolean;
	error: Error | null;
	rows: TzRuleView[];
	onRetry: () => void;
	onDelete: (row: TzRuleView) => void;
	deletingKey: string | null;
}

function RulesTable(props: RulesTableProps) {
	const {
		campaignId,
		isLoading,
		isError,
		error,
		rows,
		onRetry,
		onDelete,
		deletingKey,
	} = props;

	if (campaignId.trim().length === 0) {
		return (
			<div className="rounded-[8px] border border-dashed border-zinc-200 bg-zinc-50/50 px-6 py-10 text-center text-sm text-zinc-500">
				Enter a campaign ID to load its rules.
			</div>
		);
	}

	if (isError) {
		return (
			<div className="rounded-[8px] border border-red-200 bg-red-50/60 p-4">
				<p className="text-sm text-red-700">
					{error?.message ?? "Failed to load rules."}
				</p>
				<button
					type="button"
					onClick={onRetry}
					className="mt-2 inline-flex items-center rounded-[6px] border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50"
				>
					Retry
				</button>
			</div>
		);
	}

	if (isLoading) {
		return (
			<div className="rounded-[8px] border border-zinc-200 bg-white p-6 text-sm text-zinc-500">
				Loading rules…
			</div>
		);
	}

	if (rows.length === 0) {
		return (
			<div className="rounded-[8px] border border-dashed border-zinc-200 bg-zinc-50/50 px-6 py-10 text-center text-sm text-zinc-500">
				No rules saved for this campaign yet.
			</div>
		);
	}

	// Sort: default (*) first, then alphabetic affiliate_id.
	const sorted = [...rows].sort((a, b) => {
		if (a.affiliate_id === CAMPAIGN_DEFAULT_AFFILIATE) return -1;
		if (b.affiliate_id === CAMPAIGN_DEFAULT_AFFILIATE) return 1;
		return a.affiliate_id.localeCompare(b.affiliate_id);
	});

	return (
		<div className="overflow-hidden rounded-[8px] border border-zinc-200 bg-white shadow-sm">
			<table className="w-full text-sm">
				<thead>
					<tr className="border-b border-zinc-200 bg-zinc-50/70 text-left text-xs font-medium uppercase tracking-wide text-zinc-500">
						<th className="px-4 py-2.5">Affiliate</th>
						<th className="px-4 py-2.5">Timezone</th>
						<th className="px-4 py-2.5">Updated</th>
						<th className="w-20 px-4 py-2.5 text-right">Actions</th>
					</tr>
				</thead>
				<tbody>
					{sorted.map((row) => {
						const key = `${row.campaign_id}::${row.affiliate_id}`;
						const isDeleting = deletingKey === key;
						return (
							<tr
								key={row.id}
								className="border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50/40"
							>
								<td className="px-4 py-3 font-medium text-zinc-800">
									{row.affiliate_id === CAMPAIGN_DEFAULT_AFFILIATE ? (
										<span className="inline-flex items-center gap-2">
											<span className="rounded-full bg-gradient-to-r from-emerald-500 to-sky-500 px-2 py-0.5 text-xs font-semibold text-white">
												Default
											</span>
											<span className="text-zinc-500">*</span>
										</span>
									) : (
										<span className="font-mono text-xs text-zinc-700">
											{row.affiliate_id}
										</span>
									)}
								</td>
								<td className="px-4 py-3 font-mono text-xs text-zinc-700">
									{row.tz}
								</td>
								<td className="px-4 py-3 text-xs text-zinc-500">
									{formatTimestamp(row.updated_at)}
								</td>
								<td className="px-4 py-3 text-right">
									<button
										type="button"
										onClick={() => onDelete(row)}
										disabled={isDeleting}
										className="inline-flex items-center rounded-[6px] border border-zinc-200 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 transition hover:border-red-300 hover:bg-red-50 hover:text-red-700 focus:outline-none focus:ring-2 focus:ring-red-400/30 disabled:cursor-not-allowed disabled:opacity-60"
										aria-label={`Delete rule for ${labelAffiliate(row.affiliate_id)}`}
									>
										{isDeleting ? "Deleting…" : "Delete"}
									</button>
								</td>
							</tr>
						);
					})}
				</tbody>
			</table>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Resolve panel
// ---------------------------------------------------------------------------

interface ResolvePanelProps {
	campaignId: string;
	onCampaignChange: (id: string) => void;
}

function ResolvePanel({ campaignId, onCampaignChange }: ResolvePanelProps) {
	const [affiliate, setAffiliate] = useState<string>("");
	const [when, setWhen] = useState<string>(() => isoNowMinutes());
	const [errors, setErrors] = useState<Record<string, string>>({});
	const [result, setResult] = useState<ResolveTzResponse | null>(null);

	const campaignFieldId = useId();
	const affiliateFieldId = useId();
	const whenFieldId = useId();

	useEffect(() => {
		// Clear stale results when the campaign context changes.
		setResult(null);
	}, [campaignId]);

	const resolve = useMutation({
		mutationFn: resolveTz,
		onSuccess: (res) => {
			setResult(res);
			toast.success(`Resolved via ${describeSource(res.source)}`);
		},
		onError: (err: unknown) => {
			setResult(null);
			toast.error(friendlyError(err, "Resolve failed."));
		},
	});

	function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const parse = resolveFormSchema.safeParse({
			campaignId,
			affiliateId: affiliate,
			utcDatetimeLocal: when,
		});
		if (!parse.success) {
			setErrors(parse.errors);
			return;
		}
		setErrors({});
		resolve.mutate(parse.data);
	}

	return (
		<div className="grid gap-6 lg:grid-cols-[1.1fr_1fr]">
			<form
				onSubmit={handleSubmit}
				className="rounded-[8px] border border-zinc-200 bg-white p-6 shadow-sm"
				aria-label="Resolve timezone form"
			>
				<div className="grid gap-4">
					<div>
						<label
							htmlFor={campaignFieldId}
							className="mb-1 block text-xs font-medium text-zinc-600"
						>
							Campaign ID
						</label>
						<input
							id={campaignFieldId}
							type="text"
							value={campaignId}
							onChange={(e) => onCampaignChange(e.target.value)}
							placeholder="camp_abc123"
							autoComplete="off"
							className="w-full rounded-[6px] border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-900 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500/50"
						/>
						{errors.campaignId ? (
							<p className="mt-1 text-xs text-red-600">{errors.campaignId}</p>
						) : null}
					</div>

					<div>
						<label
							htmlFor={affiliateFieldId}
							className="mb-1 block text-xs font-medium text-zinc-600"
						>
							Affiliate ID
						</label>
						<input
							id={affiliateFieldId}
							type="text"
							value={affiliate}
							onChange={(e) => setAffiliate(e.target.value)}
							placeholder={`${CAMPAIGN_DEFAULT_AFFILIATE} (campaign default)`}
							autoComplete="off"
							className="w-full rounded-[6px] border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-900 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500/50"
						/>
					</div>

					<div>
						<label
							htmlFor={whenFieldId}
							className="mb-1 block text-xs font-medium text-zinc-600"
						>
							UTC timestamp
						</label>
						<input
							id={whenFieldId}
							type="datetime-local"
							value={when}
							onChange={(e) => setWhen(e.target.value)}
							className="w-full rounded-[6px] border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-900 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500/50"
						/>
						<p className="mt-1 text-xs text-zinc-500">
							Interpreted as UTC. The API returns the local hour, day, and the
							effective timezone for the pair.
						</p>
						{errors.utcDatetimeLocal ? (
							<p className="mt-1 text-xs text-red-600">
								{errors.utcDatetimeLocal}
							</p>
						) : null}
					</div>
				</div>

				<div className="mt-5 flex items-center justify-end">
					<button
						type="submit"
						disabled={resolve.isPending}
						className="inline-flex items-center justify-center rounded-[6px] bg-gradient-to-r from-emerald-500 to-sky-500 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:from-emerald-600 hover:to-sky-600 focus:outline-none focus:ring-2 focus:ring-emerald-500/40 disabled:cursor-not-allowed disabled:opacity-60"
					>
						{resolve.isPending ? "Resolving…" : "Resolve"}
					</button>
				</div>
			</form>

			<div
				className="rounded-[8px] border border-zinc-200 bg-white p-6 shadow-sm"
				aria-live="polite"
			>
				<ResolveResult result={result} isLoading={resolve.isPending} />
			</div>
		</div>
	);
}

interface ResolveResultProps {
	result: ResolveTzResponse | null;
	isLoading: boolean;
}

function ResolveResult({ result, isLoading }: ResolveResultProps) {
	if (isLoading) {
		return <p className="text-sm text-zinc-500">Resolving…</p>;
	}
	if (!result) {
		return (
			<div>
				<h3 className="text-sm font-semibold text-zinc-800">Awaiting input</h3>
				<p className="mt-1 text-xs text-zinc-500">
					Fill the form on the left and press Resolve to see the local
					interpretation.
				</p>
			</div>
		);
	}

	const localDayLabel = new Date(
		Date.UTC(
			result.localYear,
			Math.max(0, result.localMonth - 1),
			Math.max(1, result.localDate),
		),
	).toLocaleDateString("en-US", {
		weekday: "long",
		timeZone: "UTC",
	});

	return (
		<div>
			<h3 className="text-sm font-semibold text-zinc-800">Resolution</h3>
			<p className="mt-1 text-xs text-zinc-500">{describeSource(result.source)}</p>
			<dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
				<DescRow label="Effective tz" value={result.localTz} mono />
				<DescRow
					label="Local time"
					value={`${pad(result.localHour)}:${pad(result.localMinute)}`}
					mono
				/>
				<DescRow
					label="Local date"
					value={`${result.localYear}-${pad(result.localMonth)}-${pad(result.localDate)}`}
					mono
				/>
				<DescRow
					label="Local day"
					value={`${localDayLabel} (${result.localDay})`}
				/>
			</dl>
		</div>
	);
}

interface DescRowProps {
	label: string;
	value: string;
	mono?: boolean;
}

function DescRow({ label, value, mono }: DescRowProps) {
	return (
		<div className="rounded-[6px] border border-zinc-100 bg-zinc-50/50 px-3 py-2">
			<dt className="text-[10px] font-medium uppercase tracking-wide text-zinc-500">
				{label}
			</dt>
			<dd
				className={`mt-0.5 text-sm text-zinc-800 ${mono ? "font-mono" : ""}`}
			>
				{value}
			</dd>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function pad(n: number): string {
	return String(Math.max(0, Math.floor(n))).padStart(2, "0");
}

/**
 * Build a default datetime-local value — the current minute in UTC so the
 * picker starts in a sensible place. Shape: `YYYY-MM-DDTHH:mm`.
 */
function isoNowMinutes(): string {
	const d = new Date();
	const y = d.getUTCFullYear();
	const m = pad(d.getUTCMonth() + 1);
	const da = pad(d.getUTCDate());
	const h = pad(d.getUTCHours());
	const min = pad(d.getUTCMinutes());
	return `${y}-${m}-${da}T${h}:${min}`;
}

function friendlyError(err: unknown, fallback: string): string {
	if (err instanceof ApiError) {
		return err.message;
	}
	if (err instanceof Error && err.message) {
		return err.message;
	}
	return fallback;
}

// ---------------------------------------------------------------------------
// TanStack Router export — programmatic only. The dash scaffold (opD01)
// assembles the tree manually, so we expose a factory that the scaffold
// owner can wire in with one line:
//
//   import { createTzSchedulingRoute } from "./routes/campaigns.tz-scheduling";
//   const tzSchedulingRoute = createTzSchedulingRoute(rootRoute);
//   rootRoute.addChildren([..., tzSchedulingRoute]);
//
// A `<Link to="/campaigns/tz-scheduling">` becomes valid once registered.
// ---------------------------------------------------------------------------

// biome-ignore lint/suspicious/noExplicitAny: RootRoute generics are noisy
// and the scaffold consumer only needs a simple "pass the parent" ergonomic.
export function createTzSchedulingRoute(
	parent: RootRoute<any, any, any, any, any, any, any, any, any>,
) {
	return createRoute({
		getParentRoute: () => parent,
		path: "/campaigns/tz-scheduling",
		component: TzSchedulingPage,
	});
}

export { TzSchedulingPage };
export default TzSchedulingPage;
